import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import { UserRole } from '../../Enums/UserRole';
import usePortalService from '../../services/PortalService';
import { Candidate } from '../../types';
import styled from 'styled-components';
import { CandidateCard } from '../../Components/CandidateCards/CandidateCard';
import sort0 from '../../images/sort0.svg';
import sort1 from '../../images/sort1.svg';
import { UserTour } from '../../Components/UserTour';
import { LoaderWithTimer } from '../../Components/LoaderWithTimer';
import { AutoComplete, TabsProps, TourProps, Tag, Tabs, Pagination, Button } from 'antd';
import type { PaginationProps } from 'antd';

const StyledCandidates = styled.div`
    .inner-tab-container {
        margin-top: 40px;
    }

    .ant-tabs {
        margin-top: 24px;
    }

    .ant-select {
        height: 40px;
    }

    .inputs-container {
        overflow-x: clip;
        overflow-y: auto;
        padding-right: 10px;
        scroll-padding-right: 10px;
        width: 100%;
    }

    .filter-container .different-edges-container {
        padding-top: 12px;
        border-top: 0.5px solid #D1D6DC;
    }

    @media screen and (max-width: 768px) {
        .filter-container {
            position: static;
            width: 100%;
            padding-right: 0px;
        }
            
        .inputs-container {
            padding-right: 0px;
        }
    }  

`

const StyledTag = styled(Tag)`
    display: inline-flex;
    max-width: 200px;
    overflow: hidden;

    .tag-content {
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
    }
`

type SelectedItemType = {
    id: number,
    name: string
};

type FilterValueType = {
    value: string,
}

export const Candidates = ({role} : {role: string}) => {
    const { getAllCities, getAllInstitutions, getAllSpecialityNames, getAllSpecialityNumbers, listCandidates } = usePortalService();

    const inputsRef = useRef<HTMLDivElement>(null)
    const buttonsRef = useRef<HTMLDivElement>(null)
    const filtersRef = useRef(null)
    
    let nextTagId = 0;

    const [selectedTab, setSelectedTab] = useState('0');
    const [selectedPage, setSelectedPage] = useState(1);
    const [selectedPageSize, setSelectedPageSize] = useState(20);
    const [totalCandidatesCount, setTotalCandidatesCount] = useState(0);
    const [ratingSortOrder, setRatingSortOrder] = useState(1);

    const [isLoading, setIsLoading] = useState(false);

    const [candidates, setCandidates] = useState<Candidate[]>([]);
    const [candidatesError, setCandidatesError] = useState<boolean>(false);

    const [city, setCity] = useState('');
    const [selectedCities, setSelectedCities] = useState<SelectedItemType[]>([]);
    const [isCitiesLoaded, setIsCitiesLoaded] = useState(false);
    const [citiesList, setCitiesList] = useState<FilterValueType[]>([]);

    const [educationLevel, setEducationLevel] = useState('');
    const [selectedEducationLevels, setSelectedEducationLevels] = useState<SelectedItemType[]>([]);
    const educationLevelList = [
        { value: 'СПО' },
        { value: 'Бакалавр ВО' },
        { value: 'Магистр ВО' },
        { value: 'Специалист ВО' },
        { value: 'Аспирантура (исследователь, кандидат наук) ВО' },
    ];

    const [institute, setInstitute] = useState('');
    const [selectedInstitutions, setSelectedInstitutions] = useState<SelectedItemType[]>([]);
    const [isInstitutesLoaded, setIsInstitutesLoaded] = useState(false);
    const [institutesList, setInstitutesList] = useState<FilterValueType[]>([]);

    const [specializationName, setSpecializationName] = useState('');
    const [selectedSpecializationNames, setSelectedSpecializationNames] = useState<SelectedItemType[]>([]);
    const [isSpecializationNameLoaded, setIsSpecializationNameLoaded] = useState(false);
    const [specializationNameList, setSpecializationNameList] = useState<FilterValueType[]>([]);

    const [specializationCode, setSpecializationCode] = useState('');
    const [selectedSpecializationCodes, setSelectedSpecializationCodes] = useState<SelectedItemType[]>([]);
    const [isSpecializationCodeLoaded, setIsSpecializationCodeLoaded] = useState(false);
    const [specializationCodeList, setSpecializationCodeList] = useState<FilterValueType[]>([]);

    const items: TabsProps['items'] = role === UserRole.Manager ? [
        {
            key: '0',
            label: `Все кандидаты`,
        },
        {
            key: '1',
            label: `Избранные`,
        },
        {
            key: '2',
            label: `Хотят работать`,
        }] : [
        {
            key: '0',
            label: `Все кандидаты`,
        }];

    const steps: TourProps['steps'] = [
        {
            title: '',
            description: 'Вы можете отфильтровать кандидатов по городу, учебному заведению, специализации и уровню образования',
            target: () => filtersRef.current
        }
    ]
    
    const tabHandler = (key: string) => {
        setSelectedTab(key);
        setSelectedPage(1);

        loadCandidates({filterOption: key});

        sessionStorage.setItem('candidatesFilterProfilesOption', key);
        sessionStorage.setItem('candidatesSelectedPage', '1');
    }

    const pageHandler: PaginationProps["onChange"] = (page, pageSize) => {
        setSelectedPage(page);
        loadCandidates({page, pageSize: pageSize});
        sessionStorage.setItem('candidatesSelectedPage', JSON.stringify(page));
    }

    const changeSizeHandler: PaginationProps["onShowSizeChange"] = (current, size) => {
        setSelectedPageSize(size);
        loadCandidates({pageSize: size});
    }

    const ratingSortHandler = () => {
        setRatingSortOrder(ratingSortOrder === 0 ? 1 : 0);
    }

    const findHandler = () => {
        setSelectedPage(1);
        loadCandidates({page: 1});
        saveFilters();
    }

    const resetHandler = () => {
        setCity('');
        setSelectedCities([]);
        setEducationLevel('');
        setSelectedEducationLevels([]);
        setInstitute('');
        setSelectedInstitutions([]);
        setSpecializationName('');
        setSelectedSpecializationNames([]);
        setSpecializationCode('');
        setSelectedSpecializationCodes([]);
        setRatingSortOrder(1);
        setSelectedPage(1);

        sessionStorage.removeItem('candidatesFilters');
        sessionStorage.removeItem('candidatesFiltersCities');
        sessionStorage.removeItem('candidatesEducationLevels');
        sessionStorage.removeItem('candidatesSelectedIntitutions');
        sessionStorage.removeItem('candidatesSelectedSpecializationNames');
        sessionStorage.removeItem('candidatesSelectedSpecializationCodes');
        sessionStorage.removeItem('candidatesSortOrder');
        sessionStorage.removeItem('candidatesSelectedPage');
        
        loadCandidates({page: 1, filterOption: "0", cities: [], educationLevels: [], educationalInstitutions: [], specialtyNames: [], specialtyNumbers: []});
    }

    const saveFilters = () => {
        sessionStorage.setItem('candidatesFilters', 'true');
        sessionStorage.setItem('candidatesFilterProfilesOption', selectedTab);
        sessionStorage.setItem('candidatesFiltersCities', JSON.stringify(selectedCities));
        sessionStorage.setItem('candidatesEducationLevels', JSON.stringify(selectedEducationLevels));
        sessionStorage.setItem('candidatesSelectedIntitutions', JSON.stringify(selectedInstitutions));
        sessionStorage.setItem('candidatesSelectedSpecializationNames', JSON.stringify(selectedSpecializationNames));
        sessionStorage.setItem('candidatesSelectedSpecializationCodes', JSON.stringify(selectedSpecializationCodes));
        sessionStorage.setItem('candidatesSortOrder', JSON.stringify(ratingSortOrder));
        sessionStorage.setItem('candidatesSelectedPage', '1');
    }

    const getCities = async () => {
        if (!isCitiesLoaded) {
            const result = await getAllCities();
            if (result.success) {
                setCitiesList(result.cities.map((item: FilterValueType) => ({ value: item })));
                setIsCitiesLoaded(true)
            }
        }
    }

    const getInstitutes = async () => {
        if (!isInstitutesLoaded) {
            const result = await getAllInstitutions();
            if (result.success) {
                setInstitutesList(result.educationalInstitutions.map((item: FilterValueType) => ({ value: item })));
                setIsInstitutesLoaded(true)
            }
        }
    }

    const getSpecializationsName = async () => {
        if (!isSpecializationNameLoaded) {
            const result = await getAllSpecialityNames();
            if (result.success) {
                setSpecializationNameList(result.specialityNames.map((item: FilterValueType) => ({ value: item })));
                setIsSpecializationNameLoaded(true)
            }
        }
    }

    const getSpecializationsCode = async () => {
        if (!isSpecializationCodeLoaded) {
            const result = await getAllSpecialityNumbers();
            if (result.success) {
                setSpecializationCodeList(result.specialityNumbers.map((item: FilterValueType) => ({ value: item })));
            }
            setIsSpecializationCodeLoaded(true)
        }
    }

    const addTag = (e: string, list: SelectedItemType[], setList: React.Dispatch<React.SetStateAction<SelectedItemType[]>>, setField: React.Dispatch<React.SetStateAction<string>>) => {
        const ind = list.findIndex((a) => a.name === e)
        if (ind === -1) {
            setList([
                ...list,
                { id: nextTagId++, name: e}
            ])
        }
        setField('');
    }
    
    const closeTag = (name: string, list: SelectedItemType[], setList: React.Dispatch<React.SetStateAction<SelectedItemType[]>>) => {
        setList(
            list.filter(
                (a) => a.name !== name
            )
        );

        if (buttonsRef.current) buttonsRef.current.style.position = "sticky"
    };

    const loadCandidates = async ({
        page = selectedPage,
        filterOption = selectedTab,
        cities = selectedCities, 
        pageSize = selectedPageSize,
        educationLevels = selectedEducationLevels, 
        educationalInstitutions = selectedInstitutions,
        specialtyNames = selectedSpecializationNames,
        specialtyNumbers = selectedSpecializationCodes
    }) => {
        setIsLoading(true);
        const offset = (page - 1) * pageSize;

        try {
            const fetchedCandidates = await listCandidates({
                offset,
                limit: pageSize,
                sortBy: "UsageTimeInHours",
                sortOrder: ratingSortOrder,
                filter: {
                    filterOption: Number(filterOption),
                    cities: cities.map(city => city.name),
                    educationLevels: educationLevels.map(level => level.name),
                    educationalInstitutions: educationalInstitutions.map(institution => institution.name),
                    specialtyNames: specialtyNames.map(specializationName => specializationName.name),
                    specialtyNumbers: specialtyNumbers.map(specializationCode => specializationCode.name)
                }
            })
            setCandidates(fetchedCandidates.profiles.items);
            setTotalCandidatesCount(fetchedCandidates.profiles.totalCount);
        } catch (error) {
            setCandidatesError(true);
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    }

    const getFiltersFromStorage = () => {
        const filterProfilesOptionFromStorage = sessionStorage.getItem('candidatesFilterProfilesOption') || '0';
        const selectedPageFromStorage = JSON.parse(sessionStorage.getItem('candidatesSelectedPage') || '1');
        const areThereFilters = sessionStorage.getItem('candidatesFilters');
        
        setSelectedTab(filterProfilesOptionFromStorage);
        setSelectedPage(selectedPageFromStorage);

        if (areThereFilters) {
            const citiesFromStorage = JSON.parse(sessionStorage.getItem('candidatesFiltersCities') || '[]');
            const educationLevelsFromStorage = JSON.parse(sessionStorage.getItem('candidatesEducationLevels') || '[]');
            const institutionsFromStorage = JSON.parse(sessionStorage.getItem('candidatesSelectedIntitutions') || '[]');
            const specNamesFRomStorage = JSON.parse(sessionStorage.getItem('candidatesSelectedSpecializationNames') || '[]');
            const specCodesFromStorage = JSON.parse(sessionStorage.getItem('candidatesSelectedSpecializationCodes') || '[]');
            setSelectedCities(citiesFromStorage);
            setSelectedEducationLevels(educationLevelsFromStorage);
            setSelectedInstitutions(institutionsFromStorage);
            setSelectedSpecializationNames(specNamesFRomStorage);
            setSelectedSpecializationCodes(specCodesFromStorage);
            setRatingSortOrder(JSON.parse(sessionStorage.getItem('candidatesSortOrder') || '1'));

            loadCandidates({
                filterOption: filterProfilesOptionFromStorage, 
                cities: citiesFromStorage, 
                educationLevels: educationLevelsFromStorage, 
                educationalInstitutions: institutionsFromStorage, 
                specialtyNames: specNamesFRomStorage, 
                specialtyNumbers: specCodesFromStorage
            });
        } else {
            loadCandidates({page: selectedPageFromStorage, filterOption: filterProfilesOptionFromStorage});
        }
    };

    const updateButtonsPosition = () => {
        const inputs = inputsRef.current;
        const buttons = buttonsRef.current;

        if (!buttons || !inputs) return;

        const inputsRect = inputs.getBoundingClientRect();
        const buttonsRect = buttons.getBoundingClientRect();
        const distance = buttonsRect.top - inputsRect.top;

        if (distance < 0) {
            buttons.style.position = "sticky"
            return;
        }

        inputs.style.maxHeight = distance + 1 + "px";

        if (inputs.scrollHeight > inputs.clientHeight) {
            buttons.style.position = "fixed";
            inputs.style.marginBottom = "100px";
        } else {
            buttons.style.position = "sticky";
            inputs.style.marginBottom = "0px";
        }
    }

    const handleResize = () => {
        const buttons = buttonsRef.current;
        const inputs = inputsRef.current;

        if (!inputs || !buttons) return;

        const distance = (buttons.getBoundingClientRect().top - inputs.getBoundingClientRect().top)

        if (window.innerHeight - buttons.getBoundingClientRect().bottom > 10) {
            inputs.style.maxHeight = '';
        } else {
            inputs.style.maxHeight = distance + "px"
        }
    };

    useEffect(() => {
        window.addEventListener("scroll", updateButtonsPosition)
        window.addEventListener("resize", handleResize)

        return () => {
            window.removeEventListener("scroll", updateButtonsPosition)
            window.removeEventListener("resize", handleResize)
        }   
    }, []);

    useEffect(() => {
        getFiltersFromStorage();
    }, []);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [selectedPage]);

    useEffect(() => {
        if (candidates.length) {
            const scrollPosition = JSON.parse(sessionStorage.getItem('scrollPosition') || '0');
            if (scrollPosition) {
                window.scrollTo(0, parseInt(scrollPosition, 10));
                sessionStorage.removeItem('scrollPosition');
            }
        }
    }, [candidates])

    return (
        <StyledCandidates>
            <div className="inner-tab-container">
                <h1>Кандидаты</h1>
                <Tabs items={items} onChange={tabHandler} activeKey={selectedTab}/>
                <div>
                    <div ref={filtersRef} className="filter-container d-table-row d-md-inline">
                        <Form>
                            <div className="inputs-container scrollbar-light" ref={inputsRef}>
                                <Form.Group className="form-group" controlId="formCities">
                                    <Form.Label style={{marginBottom: '2px'}}> Город </Form.Label>
                                    <AutoComplete
                                        style={{width: '100%', height: '40px'}}
                                        options={citiesList}
                                        value={city}
                                        onClick={getCities}
                                        onChange={e => setCity(e)}
                                        onSelect={e => addTag(e, selectedCities, setSelectedCities, setCity)}
                                        filterOption={(inputValue, option) =>
                                            option!.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                                        }
                                    />
                                    {selectedCities.map(city => (
                                        <StyledTag 
                                            key={city.id} 
                                            closable 
                                            onClose={() => closeTag(city.name, selectedCities, setSelectedCities)}
                                            title={city.name}
                                        >
                                            {city.name}
                                        </StyledTag>
                                    ))}
                                </Form.Group>
                                <div className="form-group">
                                    <Form.Label> Уровень образования </Form.Label>
                                    <AutoComplete
                                        style={{width: '100%'}}
                                        options={educationLevelList}
                                        value={educationLevel}
                                        onChange={e => setEducationLevel(e)}
                                        onSelect={e => addTag(e, selectedEducationLevels, setSelectedEducationLevels, setEducationLevel)}
                                        filterOption={(inputValue, option) =>
                                            option!.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                                        }
                                    />
                                    {selectedEducationLevels.map(level => (
                                        <StyledTag 
                                            key={level.id}
                                            closable 
                                            onClose={() => closeTag(level.name, selectedEducationLevels, setSelectedEducationLevels)}
                                            title={level.name}
                                        >
                                            <span className="tag-content">
                                                {level.name}
                                            </span>
                                        </StyledTag>
                                    ))}
                                </div>
                                <Form.Group className="form-group" controlId="formInstitute">
                                    <Form.Label> Образовательное учреждение </Form.Label>
                                    <AutoComplete
                                        style={{width: '100%'}}
                                        options={institutesList}
                                        value={institute}
                                        onClick={getInstitutes}
                                        onChange={e => setInstitute(e)}
                                        onSelect={e => addTag(e, selectedInstitutions, setSelectedInstitutions, setInstitute)}
                                        filterOption={(inputValue, option) =>
                                            option!.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                                        }
                                    />
                                    {selectedInstitutions.map(selectInstitute => (
                                        <StyledTag 
                                            key={selectInstitute.id} 
                                            closable
                                            onClose={() => closeTag(selectInstitute.name, selectedInstitutions, setSelectedInstitutions)}
                                            title={selectInstitute.name}
                                        >
                                            <span className="tag-content">
                                                {selectInstitute.name}
                                            </span>
                                        </StyledTag>
                                    ))}
                                </Form.Group>
                                <Form.Group className="form-group" controlId="formSpecializationName">
                                    <Form.Label> Название специальности </Form.Label>
                                    <AutoComplete
                                        style={{width: '100%'}}
                                        options={specializationNameList}
                                        value={specializationName}
                                        onClick={getSpecializationsName}
                                        onChange={e => setSpecializationName(e)}
                                        onSelect={e => addTag(e, selectedSpecializationNames, setSelectedSpecializationNames, setSpecializationName)}
                                        filterOption={(inputValue, option) =>
                                            option!.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                                        }
                                    />
                                    {selectedSpecializationNames.map(specializationName => (
                                        <StyledTag 
                                            key={specializationName.id} 
                                            closable
                                            onClose={() => closeTag(specializationName.name, selectedSpecializationNames, setSelectedSpecializationNames)}
                                            title={specializationName.name}
                                        >
                                            <span className="tag-content">
                                                {specializationName.name}
                                            </span>
                                        </StyledTag>
                                    ))}
                                </Form.Group>
                                <Form.Group className="form-group" controlId="formSpecializationCode">
                                    <Form.Label> Номер специальности </Form.Label>
                                    <AutoComplete
                                        style={{width: '100%'}}
                                        options={specializationCodeList}
                                        value={specializationCode}
                                        onClick={getSpecializationsCode}
                                        onChange={e => setSpecializationCode(e)}
                                        onSelect={e => addTag(e, selectedSpecializationCodes, setSelectedSpecializationCodes, setSpecializationCode)}
                                        filterOption={(inputValue, option) =>
                                            option!.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                                        }
                                    />
                                    {selectedSpecializationCodes.map(specializationCode => (
                                        <StyledTag 
                                            key={specializationCode.id} 
                                            closable
                                            onClose={() => closeTag(specializationCode.name, selectedSpecializationCodes, setSelectedSpecializationCodes)}
                                            title={specializationCode.name}
                                        >
                                            <span className="tag-content">
                                                {specializationCode.name}
                                            </span>
                                        </StyledTag>
                                    ))}
                                </Form.Group>
                                <div style={{marginBottom: "24px"}}>
                                    <img 
                                        src={ratingSortOrder === 0 ? sort0 : sort1}
                                        onClick={ratingSortHandler}
                                        style={{cursor: 'pointer', marginRight: '8px'}}
                                    />
                                    <Form.Label> Сортировка рейтинг РТСИМ </Form.Label>
                                </div>
                            </div>
                            <div className='different-edges-container filter-buttons' ref={buttonsRef}>
                                <Button type="primary" onClick={findHandler} style={{height: "32px"}}>Найти</Button>
                                <Button type="text" onClick={resetHandler} style={{height: "32px"}} className="right-edge">Сбросить</Button>
                            </div>
                        </Form>
                    </div>
                    <div className="d-md-table-cell" style={{width: '230px'}}></div>
                    <div className="candidates-container d-table-row d-md-table-cell">
                        {(candidates.length === 0 && !candidatesError && !isLoading) && <p> Ничего не найдено. Попробуйте изменить критерии поиска. </p>}
                        {(candidatesError) && <p>Произошла ошибка при загрузке. Пожалуйста, попробуйте обновить страницу.</p>}
                        {isLoading ? (
                            Array.from({ length: 5 }).map((_, index) => (
                                <LoaderWithTimer key={index} isLoading={isLoading} skeletonStyle="candidate"/>
                              ))
                        ) : (candidates.map(candidate => (
                            <CandidateCard 
                                candidate={candidate}
                                role={role}
                                key={candidate.id}
                            />
                        )))}
                        {(!candidatesError && candidates.length > 0 && !isLoading) &&
                            <Pagination 
                                current={selectedPage}
                                className='d-flex justify-content-center'
                                total={totalCandidatesCount}
                                onChange={pageHandler}
                                onShowSizeChange={changeSizeHandler}
                                pageSize={selectedPageSize}
                                showSizeChanger
                                pageSizeOptions={[100, 50, 30, 20, 15, 10]} 
                            />
                        }
                    </div>
                </div>
            </div>
            <UserTour steps={steps} pageKey="candidates" />
        </StyledCandidates>
    );
}
